import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { Box, Divider, Grid, Paper, Typography } from "@mui/material";

import "../../settings.styles.css";
import "./menu.settings.styles.css";

import DeleteModal from "../../../../Components/Common/DeleteModal";
import EditAvailableMenuItem from "../../../../Components/popUpComponents/EditAvailableMenuItem";
import MenuListPanel from "./components/MenuListPanel";
import MenuTreeBuilder from "./components/MenuTreeBuilder";
import UserMenusPanel from "./components/UserMenusPanel";
import SettingsPanel from "../../components/SettingsPanel";
import { buildTree, flattenTree } from "./utilities/menuTree";

import { getStaffList } from "../../../../store/actions";

import {
  createSingleMenuItem,
  createSingleMenuItemSuccess,
  createSingleMenuNavigation,
  createSingleMenuNavigationSuccess,
  deleteSingleMenuItem,
  deleteSingleMenuItemSuccess,
  deleteSingleMenuNavigation,
  deleteSingleMenuNavigationSuccess,
  getMenuItemList,
  getMenuNavigationList,
  getNavigationMenu,
  getSingleNavigationMenu,
  roleBasedNavigationMenu,
  updateSingleMenuNavigation,
  updateSingleMenuNavigationSuccess,
} from "../../../../store/navigationMenu/action";

export default function MenuSettings() {
  const dispatch = useDispatch();

  const authUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("authUser")) || {};
    } catch {
      return {};
    }
  }, []);
  const MenuNav_Id = authUser?.get_staff_data?.MenuNav_Id;
  const LSRole = authUser?.data?.first_name;

  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Top-level tabs: the drag-and-drop builder vs. the read-only "who has what"
  // overview of user -> menu assignments.
  const [activeTab, setActiveTab] = useState("builder");

  // delete-menu modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // edit-available-item modal
  const [editItemModal, setEditItemModal] = useState(false);
  const [editItemTarget, setEditItemTarget] = useState({});

  const {
    menuNavigationListAPI,
    availableMenuItemsAPI,
    singleNavigationMenuAPI,
    staffListAPI,
    staffListLoading,
  } = useSelector((state) => ({
    menuNavigationListAPI:
      state.NavigationMenu.menuNavigationList?.menunav_list,
    availableMenuItemsAPI: state.NavigationMenu.menuItemList?.menunavitem_list,
    singleNavigationMenuAPI: state.NavigationMenu.singleNavigationMenu,
    staffListAPI: state.Staff.staffList?.staff_list,
    staffListLoading: state.Staff.loadingGetStaffList,
  }));

  useEffect(() => {
    dispatch(getMenuNavigationList());
    dispatch(getMenuItemList());
    dispatch(getStaffList());
  }, [dispatch]);

  const menus = useMemo(
    () => [...(menuNavigationListAPI || [])].sort((a, b) => b.Id - a.Id),
    [menuNavigationListAPI]
  );

  const refreshLists = () => {
    dispatch(getMenuNavigationList());
    dispatch(getMenuItemList());
  };
  const refreshLiveSidebar = () => {
    if (MenuNav_Id) {
      dispatch(getNavigationMenu([{ menunavstructure_data: { MenuNav_Id } }]));
    }
    if (LSRole) dispatch(roleBasedNavigationMenu(LSRole));
  };

  // ---- selection ----
  const handleSelectMenu = (id) => {
    setSelectedId(String(id));
    dispatch(getSingleNavigationMenu({ id }));
  };

  // Only hand the builder data once it matches the selected menu (avoids a
  // flash of the previously-selected menu while the fetch is in flight).
  const selectedMenuData =
    singleNavigationMenuAPI?.menunav_data &&
    String(singleNavigationMenuAPI.menunav_data.Id) === String(selectedId)
      ? singleNavigationMenuAPI.menunav_data
      : null;
  const selectedMenuList = selectedMenuData
    ? singleNavigationMenuAPI?.menunavstructure_list || []
    : [];

  // Task 24886 - a menu CRMit rolled out from ZCRM_Master carries the link
  // back to its master definition. The company cannot edit it (the API
  // refuses), so the builder renders it inert and offers the one move that is
  // available: clone it into a company copy.
  const selectedMenuRow = (menus || []).find(
    (m) => String(m.Id) === String(selectedId)
  );
  const selectedIsGlobal = Boolean(
    selectedMenuRow?.MasterMenuNav_Id ||
      Number(selectedMenuRow?.FlagMasterLockNoChanges) === 1
  );

  // ---- create menu ----
  const handleCreateMenu = (name) => {
    dispatch(
      createSingleMenuNavigation([{ Name: name, menunavstructure_list: [] }])
    );
  };
  // Cloning goes through create-with-structure rather than /menunav/duplicate:
  // that endpoint copies MenuNavStructure rows verbatim, ParentId included, so
  // a two-level menu's children would hang off the ORIGINAL rows' ids. create()
  // rebuilds the parents as it inserts.
  const [selectOnCreate, setSelectOnCreate] = useState(false);
  const handleDuplicateMenu = (name, structure) => {
    setSelectOnCreate(true);
    dispatch(
      createSingleMenuNavigation([
        { Name: name, menunavstructure_list: structure },
      ])
    );
  };
  // Change #100366 - the list row's copy button. The list rows carry no
  // structure, so the menu is selected (which fetches it) and the clone fires
  // once that fetch has landed for the same id.
  const [pendingRowDuplicateId, setPendingRowDuplicateId] = useState(null);
  const handleRowDuplicate = (menu) => {
    setPendingRowDuplicateId(String(menu.Id));
    handleSelectMenu(menu.Id);
  };
  useEffect(() => {
    if (!pendingRowDuplicateId || !selectedMenuData) return;
    if (String(selectedMenuData.Id) !== pendingRowDuplicateId) return;
    setPendingRowDuplicateId(null);
    handleDuplicateMenu(
      `${(selectedMenuData.Name || "").trim()} (copy)`,
      buildTree(flattenTree(selectedMenuList))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRowDuplicateId, selectedMenuData]);

  const { createMenuSuccessAPI } = useSelector((state) => ({
    createMenuSuccessAPI:
      state.NavigationMenu.createSingleMenuNavigationSuccess,
  }));
  useEffect(() => {
    if (isEmpty(createMenuSuccessAPI)) return;
    if (createMenuSuccessAPI.status === 200) {
      toast.success(createMenuSuccessAPI.message);
      refreshLists();
      // A clone you cannot see is a clone you will make twice.
      if (selectOnCreate && createMenuSuccessAPI.MenuNav_Id) {
        handleSelectMenu(createMenuSuccessAPI.MenuNav_Id);
      }
      setSelectOnCreate(false);
    } else {
      toast.error(createMenuSuccessAPI.message);
    }
    dispatch(createSingleMenuNavigationSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createMenuSuccessAPI]);

  // ---- delete menu ----
  const askDeleteMenu = (id) => {
    setPendingDeleteId(id);
    setDeleteModal(true);
  };
  const confirmDeleteMenu = () => {
    dispatch(deleteSingleMenuNavigation([pendingDeleteId]));
  };
  const { deleteMenuSuccessAPI } = useSelector((state) => ({
    deleteMenuSuccessAPI:
      state.NavigationMenu.deleteSingleMenuNavigationSuccess,
  }));
  useEffect(() => {
    if (isEmpty(deleteMenuSuccessAPI)) return;
    if (deleteMenuSuccessAPI.status === 200) {
      toast.success(deleteMenuSuccessAPI.message);
      if (String(pendingDeleteId) === String(selectedId)) setSelectedId(null);
      refreshLists();
    } else {
      toast.error(deleteMenuSuccessAPI.message);
    }
    setDeleteModal(false);
    setPendingDeleteId(null);
    dispatch(deleteSingleMenuNavigationSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteMenuSuccessAPI]);

  // ---- save menu structure ----
  const handleSaveMenu = (structure, name) => {
    setSaving(true);
    dispatch(
      updateSingleMenuNavigation([
        [{ Id: selectedId, Name: name, menunavstructure_list: structure }],
      ])
    );
  };
  const { updateMenuSuccessAPI } = useSelector((state) => ({
    updateMenuSuccessAPI:
      state.NavigationMenu.updateSingleMenuNavigationSuccess,
  }));
  useEffect(() => {
    if (isEmpty(updateMenuSuccessAPI)) return;
    setSaving(false);
    if (updateMenuSuccessAPI.status === 200) {
      toast.success(updateMenuSuccessAPI.message || "Menu saved");
      if (selectedId) dispatch(getSingleNavigationMenu({ id: selectedId }));
      refreshLists();
      refreshLiveSidebar();
    } else {
      toast.error(updateMenuSuccessAPI.message);
    }
    dispatch(updateSingleMenuNavigationSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMenuSuccessAPI]);

  // ---- create available item ----
  const handleCreateItem = (data) => {
    dispatch(createSingleMenuItem([data]));
  };
  const { createItemSuccessAPI } = useSelector((state) => ({
    createItemSuccessAPI: state.NavigationMenu.createSingleMenuItemSuccess,
  }));
  useEffect(() => {
    if (isEmpty(createItemSuccessAPI)) return;
    if (createItemSuccessAPI.status === 200) {
      toast.success(createItemSuccessAPI.message);
      dispatch(getMenuItemList());
    } else {
      toast.error(createItemSuccessAPI.message);
    }
    dispatch(createSingleMenuItemSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createItemSuccessAPI]);

  // ---- delete available item ----
  const handleDeleteAvailableItem = (id) => {
    dispatch(deleteSingleMenuItem([id]));
  };
  const { deleteItemSuccessAPI } = useSelector((state) => ({
    deleteItemSuccessAPI: state.NavigationMenu.deleteSingleMenuItemSuccess,
  }));
  useEffect(() => {
    if (isEmpty(deleteItemSuccessAPI)) return;
    if (deleteItemSuccessAPI.status === 200) {
      toast.success(deleteItemSuccessAPI.message);
      dispatch(getMenuItemList());
    } else {
      toast.error(deleteItemSuccessAPI.message);
    }
    dispatch(deleteSingleMenuItemSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteItemSuccessAPI]);

  // ---- edit available item ----
  const handleEditAvailableItem = (item) => {
    setEditItemTarget(item);
    setEditItemModal(true);
  };

  return (
    <>
      {editItemModal && (
        <EditAvailableMenuItem
          modal={editItemModal}
          setModal={setEditItemModal}
          toggle={() => setEditItemModal((s) => !s)}
          selectedAvailableMenuItem={editItemTarget}
        />
      )}
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onDeleteClick={confirmDeleteMenu}
          onCloseClick={() => {
            setDeleteModal(false);
            setPendingDeleteId(null);
          }}
        />
      )}

      <SettingsPanel
        title="Menus"
        description="Build the navigation menu your team sees, and see who has what"
        tabs={[
          {
            value: "builder",
            label: "Menu Builder",
            icon: "fa-solid fa-bars",
          },
          {
            value: "users",
            // Task 24887 - "User Menus" read as a KIND of menu. This tab is
            // not a menu at all, it is the mapping of people to menus, and the
            // name now says so. The /user-menus route is unchanged, so existing
            // links and the deep-link routing from Task 24779 still work.
            label: "User Menu Assignment",
            icon: "fa-solid fa-users",
            count: (staffListAPI || []).length || undefined,
          },
        ]}
        value={activeTab}
        onChange={setActiveTab}
      >
        {activeTab === "builder" ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <MenuListPanel
                    menus={menus}
                    selectedId={selectedId}
                    onSelect={handleSelectMenu}
                    onCreate={handleCreateMenu}
                    onDelete={askDeleteMenu}
                    onDuplicate={handleRowDuplicate}
                    /* Task 24886 */
                    scopePills
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={8} lg={9}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, minHeight: 300 }}
                >
                  {selectedMenuData ? (
                    <MenuTreeBuilder
                      key={selectedId}
                      menuData={selectedMenuData}
                      initialList={selectedMenuList}
                      availableItemsAll={availableMenuItemsAPI}
                      saving={saving}
                      onSave={handleSaveMenu}
                      onDuplicate={handleDuplicateMenu}
                      onCreateItem={handleCreateItem}
                      onEditItem={handleEditAvailableItem}
                      onDeleteAvailableItem={handleDeleteAvailableItem}
                      /* Task 24886 */
                      readOnly={selectedIsGlobal}
                      readOnlyNotice="Global menu - managed by CRMit"
                      duplicateLabel={
                        selectedIsGlobal ? "Clone to company copy" : "Duplicate"
                      }
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 260,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.disabled",
                      }}
                    >
                      <i
                        className="fa-solid fa-bars"
                        style={{ fontSize: 32, marginBottom: 12 }}
                      />
                      <Typography variant="body1">
                        Select a menu on the left, or create a new one to start
                        building.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ mt: 3 }} />
          </>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <UserMenusPanel
              menus={menus}
              staffList={staffListAPI}
              loading={staffListLoading}
              /* Task 24688 - the panel can now reassign a user's menu, and
                 the staff list lives up here, so it has to be re-fetched for
                 the row to show the value that was just saved. */
              onChanged={() => dispatch(getStaffList())}
            />
          </Paper>
        )}
      </SettingsPanel>
    </>
  );
}
